// =====================================================
// push.ts — fichier UNIQUE (remplace push.native.ts + push.web.ts)
//
// Pourquoi un seul fichier ?
// Expo SDK 50 static export bundle AUSSI les .native.ts pour le
// renderer SSR Node — l'extension seule ne suffit pas.
//
// Solution : require() dynamique à l'intérieur des branches
// conditionnelles → Metro ne traverse pas le code mort.
//
// Mobile : getDevicePushTokenAsync() → token FCM natif Android
//          envoyé au backend → Firebase Admin SDK → FCM → appareil
// Web    : VAPID → navigateur (inchangé)
//
// MULTILINGUE (fr/en/es/pt) :
// Le titre/corps des notifications est déjà traduit côté serveur.
// Les LIBELLÉS DE BOUTONS, eux, sont choisis côté client :
//   - Web    : le service worker les choisit lui-même, à condition
//              de connaître la langue courante → on la lui transmet
//              ici, juste après son enregistrement (registerServiceWorker).
//   - Mobile : les catégories de notification (avec leurs boutons)
//              sont enregistrées une seule fois au démarrage de l'app
//              → on lit la langue sauvegardée localement (AsyncStorage,
//              via services/language.ts) et on la transmet à
//              registerNotificationCategories() avant l'enregistrement.
// =====================================================

import { Platform }  from 'react-native';
import AsyncStorage  from '@react-native-async-storage/async-storage';

const BACKEND_URL  = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const VAPID_PUBLIC = 'BO8leq_M-yyp1_E0XakKIkDumAsGPjrO4-0T5QzcrcNcz1wevDDqEoZQDHaf8BTodvHCnKGmHGeFpN0GB-25pXQ';
const MOBILE_KEY   = 'harmonia_push_mobile_registered';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// ── Auth helper ────────────────────────────────────────────────────────────────
type GetAuth = () => Promise<{ user_id: string; access_token: string } | null>;

async function checkDB(auth: { user_id: string; access_token: string }, platform: string): Promise<boolean> {
  try {
    const res = await fetch(`${BACKEND_URL}/push`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        action:       'checkSubscription',
        platform,
        user_id:      auth.user_id,
        access_token: auth.access_token,
      }),
    });
    if (res.ok) {
      const d = await res.json();
      return d.subscribed === true;
    }
  } catch {}
  return false;
}

// ══════════════════════════════════════════════════════════════════════════════
// SERVICE WORKER — enregistrement indépendant de l'authentification
//
// Doit être appelé le plus tôt possible sur toute page web (y compris
// la landing publique, avant login) pour que Chrome considère le site
// comme "installable" et déclenche l'event beforeinstallprompt.
//
// Idempotent : si déjà enregistré (par exemple ensuite par initPushWeb
// une fois l'utilisateur connecté), le navigateur réutilise la même
// registration sans effet de bord ni double instance.
// ══════════════════════════════════════════════════════════════════════════════
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return null;
  if (!('serviceWorker' in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('[SW] Enregistré avec succès, scope :', registration.scope);

    // Transmettre la langue actuelle au service worker dès l'enregistrement,
    // pour qu'il puisse choisir les bons libellés de boutons dès la première
    // notification (même si l'utilisateur n'a pas retouché son sélecteur de
    // langue depuis un précédent redémarrage du service worker).
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { getSavedLanguage } = require('./storage'); // point d'entrée unique (web+mobile)
      const lang = await getSavedLanguage();
      if (lang) {
        await navigator.serviceWorker.ready;
        registration.active?.postMessage({ type: 'SET_LANGUAGE', lang });
      }
    } catch (err) {
      console.warn('[SW] Impossible de transmettre la langue au service worker :', err);
    }

    return registration;
  } catch (err) {
    console.warn('[SW] Échec de l\'enregistrement :', err);
    return null;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// POINT D'ENTRÉE UNIQUE
// ══════════════════════════════════════════════════════════════════════════════
export async function initPush(getAuth: GetAuth): Promise<void> {
  if (Platform.OS === 'web') {
    return initPushWeb(getAuth);
  } else {
    return initPushNative(getAuth);
  }
}

export async function removePush(getAuth: GetAuth): Promise<void> {
  if (Platform.OS === 'web') {
    return removePushWeb(getAuth);
  } else {
    return removePushNative(getAuth);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// WEB — Browser Push API + VAPID (inchangé)
// ══════════════════════════════════════════════════════════════════════════════
async function initPushWeb(getAuth: GetAuth): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('[Push.web] Navigateur non supporté');
    return;
  }

  try {
    const auth = await getAuth();
    if (!auth) return;

    // Réutilise l'enregistrement existant (fait par registerServiceWorker
    // dès l'arrivée sur la landing) au lieu de ré-enregistrer.
    let registration = await navigator.serviceWorker.getRegistration('/service-worker.js');
    if (!registration) {
      registration = (await registerServiceWorker()) ?? undefined;
    }
    if (!registration) {
      console.warn('[Push.web] Service worker indisponible');
      return;
    }
    await navigator.serviceWorker.ready;

    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      fetch(`${BACKEND_URL}/push`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action:       'updateLastSeen',
          platform:     'web',
          endpoint:     existing.endpoint,
          user_id:      auth.user_id,
          access_token: auth.access_token,
        }),
      }).catch(() => {});
      console.log('[Push.web] Déjà abonné (navigateur) — last_seen_at mis à jour');
      return;
    }

    const alreadyInDB = await checkDB(auth, 'web');
    const permState = typeof Notification !== 'undefined' ? Notification.permission : 'default';

    if (alreadyInDB && permState === 'denied') {
      console.log('[Push.web] Permission refusée sur ce navigateur');
      return;
    }

    if (permState !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('[Push.web] Permission refusée');
        return;
      }
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly:      true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
    });

    const sub      = subscription.toJSON();
    const endpoint = sub.endpoint!;
    const p256dh   = sub.keys?.p256dh ?? '';
    const authKey  = sub.keys?.auth   ?? '';

    let oldEndpoint: string | null = null;
    if (alreadyInDB) {
      const epRes = await fetch(`${BACKEND_URL}/push`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action:       'getEndpoint',
          platform:     'web',
          user_id:      auth.user_id,
          access_token: auth.access_token,
        }),
      }).catch(() => null);
      if (epRes?.ok) {
        const epData = await epRes.json();
        oldEndpoint = epData.endpoint ?? null;
      }
    }

    const action     = alreadyInDB ? 'update' : 'subscribe';
    const bodyExtras = oldEndpoint ? { old_endpoint: oldEndpoint } : {};

    const res = await fetch(`${BACKEND_URL}/push`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        action,
        platform:     'web',
        endpoint,
        p256dh,
        auth:         authKey,
        user_id:      auth.user_id,
        access_token: auth.access_token,
        ...bodyExtras,
      }),
    });

    if (res.ok) {
      console.log(alreadyInDB
        ? `[Push.web] ✅ Nouveau navigateur enregistré (update${oldEndpoint ? ' + ancien désactivé' : ''})`
        : '[Push.web] ✅ Premier abonnement enregistré'
      );
    } else {
      console.warn('[Push.web] ❌ Erreur enregistrement');
    }
  } catch (err) {
    console.warn('[Push.web] Erreur :', err);
  }
}

async function removePushWeb(getAuth: GetAuth): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration('/service-worker.js');
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    const auth = await getAuth();
    if (auth) {
      await fetch(`${BACKEND_URL}/push`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ action: 'unsubscribe', platform: 'web', endpoint, ...auth }),
      });
    }
  } catch (err) {
    console.warn('[Push.web] Erreur suppression :', err);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// NATIVE — FCM Android via getDevicePushTokenAsync
//
// Différence clé avec l'ancienne version :
//   Avant : getExpoPushTokenAsync({ projectId, accessToken })
//           → token Expo (ExponentPushToken[xxx]) → serveurs Expo → FCM
//   Maintenant : getDevicePushTokenAsync()
//           → token FCM natif → ton backend → Firebase Admin → FCM directement
//
// Plus besoin de projectId ni d'accessToken côté app.
// ══════════════════════════════════════════════════════════════════════════════
async function initPushNative(getAuth: GetAuth): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Notifications = require('expo-notifications');

    // Configurer le handler de notifications reçues
    Notifications.setNotificationHandler({
      handleNotification: async () => ({
        shouldShowBanner: true,  // SDK 52+ — remplace shouldShowAlert
        shouldShowList:   true,  // SDK 52+ — affichage centre de notifs
        shouldPlaySound:  true,
        shouldSetBadge:   true,
      }),
    });

    // Récupérer la langue sauvegardée localement (AsyncStorage) avant
    // d'enregistrer les catégories — pour que les boutons d'action
    // (open/dismiss) apparaissent dans la bonne langue dès la première
    // notification reçue.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getSavedLanguage } = require('./storage'); // point d'entrée unique (web+mobile)
    const savedLang = await getSavedLanguage();

    // Enregistrer les catégories (boutons d'action par type), dans la langue
    // sauvegardée (ou français par défaut si aucune langue n'a encore été choisie).
    const { registerNotificationCategories } = require('./notificationCategories');
    await registerNotificationCategories(savedLang ?? 'fr');

    // Canal Android obligatoire
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('default', {
        name:             'Harmonia',
        importance:       Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor:       '#7C3AED',
      });
    }

    // Cache local — évite les requêtes inutiles au démarrage
    const cached = await AsyncStorage.getItem(MOBILE_KEY);
    if (cached === 'true') {
      console.log('[Push.native] Cache — déjà abonné');
      return;
    }

    const auth = await getAuth();
    if (!auth) return;

    // Vérifier en DB si un token est déjà enregistré pour cet user
    if (await checkDB(auth, 'mobile')) {
      await AsyncStorage.setItem(MOBILE_KEY, 'true');
      console.log('[Push.native] DB — déjà abonné');
      return;
    }

    // Demander la permission à l'OS
    const { status: existing } = await Notifications.getPermissionsAsync();
    let finalStatus = existing;
    if (existing !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
    if (finalStatus !== 'granted') {
      console.log('[Push.native] Permission refusée');
      return;
    }

    // ── Récupérer le token FCM natif ───────────────────────────────────────
    // getDevicePushTokenAsync() retourne le vrai token FCM de l'appareil
    // sans passer par les serveurs Expo — pas de projectId, pas d'accessToken
    console.log(`[Push.native] Plateforme ${Platform.OS} — récupération token FCM`);
    let fcmToken: string;
    try {
      const tokenData = await Notifications.getDevicePushTokenAsync();
      // tokenData = { type: 'android', data: 'fcm-token-xxx' }
      fcmToken = tokenData.data;
      console.log(`[Push.native] Token FCM (${Platform.OS}) :`, fcmToken);
    } catch (err) {
      console.warn('[Push.native] Impossible de récupérer le token FCM :', err);
      return;
    }

    // Enregistrer le token FCM en DB via le backend
    // Le champ s'appelle encore expo_push_token en DB — il stocke maintenant le token FCM
    const res = await fetch(`${BACKEND_URL}/push`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        action:          'subscribe',
        platform:        'mobile',
        expo_push_token: fcmToken,   // champ DB inchangé, contient le token FCM
        user_id:         auth.user_id,
        access_token:    auth.access_token,
      }),
    });

    if (res.ok) {
      await AsyncStorage.setItem(MOBILE_KEY, 'true');
      console.log('[Push.native] ✅ Token FCM enregistré');
    } else {
      console.warn('[Push.native] ❌ Erreur enregistrement');
    }

  } catch (err) {
    console.warn('[Push.native] Erreur :', err);
  }
}

async function removePushNative(getAuth: GetAuth): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Notifications = require('expo-notifications');

    const auth = await getAuth();
    if (!auth) {
      await AsyncStorage.removeItem(MOBILE_KEY);
      return;
    }

    // Récupérer le token FCM natif pour le désabonnement
    const tokenData = await Notifications.getDevicePushTokenAsync().catch(() => null);

    if (tokenData) {
      await fetch(`${BACKEND_URL}/push`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action:          'unsubscribe',
          platform:        'mobile',
          expo_push_token: tokenData.data,
          user_id:         auth.user_id,
          access_token:    auth.access_token,
        }),
      });
    }
    await AsyncStorage.removeItem(MOBILE_KEY);
    console.log('[Push.native] Abonnement supprimé');
  } catch (err) {
    console.warn('[Push.native] Erreur suppression :', err);
  }
}
